// Application Constants

// Admin Credentials
export const ADMIN_CREDENTIALS = {
  EMAIL: "sgupta@gmail.com",
  PASSWORD: "1234",
  NAME: "Dr. Gupta",
  USER_ID: "admin",
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  IS_LOGGED_IN: "isLoggedIn",
  USER_EMAIL: "userEmail",
  IS_ADMIN: "isAdmin",
  HOSPITAL_NAME: "hospitalName",
  USER_ID: "userId",
  ROLE: "userRole",
  AUTH_TOKEN: "authToken", // Add token storage key
} as const;

// Global (window) event names used to bridge non-React modules to React
export const AUTH_EVENTS = {
  SESSION_EXPIRED: "auth:session-expired",
} as const;

// Filter Options
export const FILTER_OPTIONS = {
  ALL: "all",
  RISK_LEVELS: {
    POOR: "poor",
    AVERAGE: "average",
    GOOD: "good",
  },
  AGE_GROUPS: {
    YOUNG: "18-35",
    MIDDLE: "36-50",
    SENIOR: "51-65",
    ELDER: "65+",
  },
  GENDERS: {
    MALE: "male",
    FEMALE: "female",
    OTHER: "other",
  },
} as const;

// Sort Options
export const SORT_OPTIONS = {
  DIRECTIONS: {
    ASC: "asc",
    DESC: "desc",
  },
  FIELDS: {
    NAME: "name",
    AGE: "age",
    LAST_SYNC: "lastSync",
    RISK_LEVEL: "riskLevel",
    BLOOD_PRESSURE: "bloodPressure",
    CREATED_AT: "createdAt",
    UPDATED_AT: "updatedAt",
  },
} as const;

export const DEFAULT_PATIENT_SORT = {
  label: "Last Updated (Newest First)",
  field: SORT_OPTIONS.FIELDS.UPDATED_AT,
  direction: SORT_OPTIONS.DIRECTIONS.DESC,
} as const;

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
  RECORDS_PER_PAGE: 10,
  MAX_LIMIT: 100,
} as const;

export const PATIENT_LIST_QUERY_KEYS = {
  SEARCH: "search",
  RISK: "risk",
  AGE: "age",
  GENDER: "gender",
  SORT: "sort",
  DIR: "dir",
  PAGE: "page",
} as const;

export const PATIENT_LIST_SEARCH_DEBOUNCE_MS = 300;

// Vital Signs
export const VITAL_SIGNS = {
  HEART_RATE: "HEART_RATE",
  BLOOD_PRESSURE: "BLOOD_PRESSURE",
  BLOOD_OXYGEN: "BLOOD_OXYGEN",
  RANGES: {
    HEART_RATE: { MIN: 60, MAX: 100, CHART_MIN: 0, CHART_MAX: 200 },
    BLOOD_PRESSURE: {
      SYSTOLIC_HIGH: 140,
      DIASTOLIC_HIGH: 90,
      SYSTOLIC_ELEVATED: 120,
      DIASTOLIC_ELEVATED: 80,
    },
    BLOOD_OXYGEN: { MIN: 95, CRITICAL: 90, CHART_MIN: 70, CHART_MAX: 105 },
  },
} as const;

// Risk Levels
export const RISK_LEVELS = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  THRESHOLDS: {
    LOW: 30,
    MEDIUM: 60,
    HIGH: 100,
  },
} as const;

// Survey Constants
export const SURVEY = {
  UNITS: {
    HEIGHT: {
      CM: "cm",
      FT_IN: "ft_in",
    },
    WEIGHT: {
      KG: "kg",
      POUNDS: "pounds",
    },
  },
  CONTACT_TYPES: {
    EMAIL: "email",
    MOBILE: "mobile",
  },
  VALIDATION: {
    EMAIL_REGEX: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    MOBILE_REGEX: /^[6-9]\d{9}$/,
    AGE_MAX_DIGITS: 2,
    WEIGHT_MAX_DIGITS: 3,
    HEIGHT_MAX_DIGITS: {
      FEET: 2,
      INCHES: 2,
      CM: 3,
    },
  },
  CONVERSIONS: {
    CM_PER_FOOT: 30.48,
    CM_PER_INCH: 2.54,
    KG_PER_POUND: 0.453592,
  },
} as const;

// UI Messages
export const MESSAGES = {
  LOADING: {
    PATIENTS: "Loading patients...",
    PATIENT_DETAILS: "Loading patient details...",
    HEALTH_METRICS: "Loading health metrics...",
    REPORT: "Loading Report...",
    SUMMARY: "Loading summary...",
    SIGNING_IN: "Signing in...",
    HOSPITALS: "Loading hospitals...",
    NOTES: "Loading notes...",
    REPORTS: "Loading reports...",
    COMPARISON: "Comparing SuperECG reports...",
  },
  ERRORS: {
    FETCH_PATIENTS: "Failed to fetch patients",
    FETCH_NOTES: "Failed to load notes. Please try again.",
    FETCH_REPORTS: "Failed to load reports. Please try again.",
    COMPARE_REPORTS: "Failed to compare reports. Please try again.",
    INVALID_CREDENTIALS: "Invalid email or password",
    LOGIN_FAILED: "Login failed. Please check your connection and try again.",
    EMAIL_EXISTS: "This email is already registered, please use another email",
    UNKNOWN_ERROR: "Unknown error",
    NO_DATA: "No data available",
  },
  SUCCESS: {
    NOTE_SAVED: "Note saved successfully",
  },
  AUTH: {
    SESSION_EXPIRED: "Your session has expired. Please log in again.",
  },
  EMPTY_STATES: {
    NO_PATIENTS_ADMIN: "No patients found",
    NO_PATIENTS_HOSPITAL: "No patients registered",
    NO_PATIENTS_FILTERED: "No patients found matching your criteria",
    NO_NOTES: "No notes yet.",
    NO_NOTES_HELPER: "Click Add Note to record the first clinical note.",
    NO_REPORTS: "No reports available.",
    NO_REPORTS_HELPER:
      "Click Upload reports to add the first report for this patient.",
  },
  COMPARE: {
    SELECT_TWO: "Select two SuperECG reports to compare.",
    TITLE: "SuperECG comparison",
  },
  VALIDATION: {
    REQUIRED_FIELD: "This field is required",
    INVALID_EMAIL: "Please enter a valid email address",
    INVALID_MOBILE: "Please enter a valid 10-digit mobile number",
    PASSWORD_MIN_LENGTH: "Password must be at least 8 characters",
    EMAIL_BLOCKED: "This email address is not allowed",
  },
} as const;

// File Paths
export const FILE_PATHS = {
  IMAGES: {
    HELIUS_LOGO: "/images/HeliusLogoWithText.svg",
  },
} as const;

const PATIENT_LIST_COLUMNS_PX = [
  { id: "name", widthPx: 173 },
  { id: "age", widthPx: 71 },
  { id: "gender", widthPx: 103 },
  { id: "riskLevel", widthPx: 118 },
  { id: "heartRate", widthPx: 126 },
  { id: "bloodPressure", widthPx: 145 },
  { id: "bloodOxygen", widthPx: 126 },
  { id: "sleep", widthPx: 151 },
  { id: "steps", widthPx: 138 },
  { id: "lastSync", widthPx: 133 },
  { id: "createdAt", widthPx: 133 },
  { id: "updatedAt", widthPx: 133 },
  { id: "actions", widthPx: 88 },
] as const;

/** Figma column proportions as % widths (sum = 100) for table-layout: fixed */
export function getPatientListColumns(includeActions: boolean) {
  const pxCols = includeActions
    ? [...PATIENT_LIST_COLUMNS_PX]
    : PATIENT_LIST_COLUMNS_PX.filter((col) => col.id !== "actions");
  const totalPx = pxCols.reduce((sum, col) => sum + col.widthPx, 0);
  const cols = pxCols.map((col) => ({
    id: col.id,
    widthPx: col.widthPx,
    widthPercent: Math.round((col.widthPx / totalPx) * 10000) / 100,
  }));
  const percentSum = cols.reduce((sum, col) => sum + col.widthPercent, 0);
  const remainder = Math.round((100 - percentSum) * 100) / 100;
  return cols.map((col, index) =>
    index === cols.length - 1
      ? { ...col, widthPercent: col.widthPercent + remainder }
      : col
  );
}

/** @deprecated Use getPatientListColumns(true) */
export const PATIENT_LIST_COLUMNS = getPatientListColumns(true);

// CSS Classes
export const CSS_CLASSES = {
  BUTTONS: {
    PRIMARY:
      "bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
    SECONDARY: "bg-gray-100 hover:bg-gray-200 text-gray-700",
    DANGER:
      "bg-rag-red text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-rag-red/40 focus:ring-offset-2",
    LOADING: "bg-gray-400 text-gray-200 cursor-not-allowed",
    PRIMARY_PATIENT:
      "inline-flex items-center justify-center min-h-11 px-4 py-2 rounded-full bg-rag-green text-white text-sm font-semibold transition-colors hover:bg-[#0B7A72] focus:outline-none focus:ring-2 focus:ring-rag-green/40 disabled:opacity-50 disabled:cursor-not-allowed",
    SECONDARY_PATIENT:
      "inline-flex items-center justify-center min-h-11 px-4 py-2 rounded-full bg-guardian-10 text-[#02040A] text-sm font-semibold transition-colors hover:bg-guardian-20 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed",
    CANCEL_MODAL:
      "inline-flex items-center justify-center min-h-11 w-full px-4 py-2 rounded-full bg-guardian-10 text-sm font-semibold text-guardian-body transition-colors hover:bg-guardian-20 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto",
    PRIMARY_MODAL:
      "inline-flex items-center justify-center min-h-11 w-full px-4 py-2 rounded-full bg-rag-green text-sm font-semibold text-white transition-colors hover:bg-[#0B7A72] focus:outline-none focus:ring-2 focus:ring-rag-green/40 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto",
    DANGER_MODAL:
      "inline-flex items-center justify-center min-h-11 w-full px-4 py-2 rounded-full bg-rag-red text-sm font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-rag-red/40 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto",
  },
  MODAL: {
    OVERLAY:
      "fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-3 sm:items-center sm:p-4",
    PANEL:
      "flex w-full max-h-[92vh] flex-col overflow-hidden rounded-xl bg-white shadow-xl",
    PANEL_SM: "max-w-md",
    PANEL_MD: "max-w-md",
    PANEL_LG: "max-w-2xl",
    PANEL_XL: "max-w-5xl",
    PANEL_UPLOAD: "w-full max-w-lg sm:max-w-2xl",
    HEADER:
      "flex flex-shrink-0 items-center justify-between border-b border-guardian-20 px-4 py-4 sm:px-6",
    TITLE: "text-xl font-bold tracking-[-0.02em] text-guardian-body",
    SUBTITLE: "mt-0.5 text-sm text-guardian-header",
    BODY: "min-h-0 flex-1 overflow-y-auto",
    FOOTER:
      "flex flex-shrink-0 flex-col-reverse gap-2 border-t border-guardian-20 p-4 sm:flex-row sm:justify-end",
    CLOSE_BTN:
      "rounded-full p-2 text-guardian-header transition-colors hover:bg-guardian-10 hover:text-guardian-body",
  },
  FORM: {
    SECTION_TITLE: "text-sm font-medium text-guardian-header mb-3",
  },
  AUTH: {
    PAGE: "min-h-screen bg-guardian-10 flex items-center justify-center px-3 py-6 sm:px-4",
    CARD: "max-w-md w-full bg-white rounded-2xl border border-guardian-20 shadow-sm p-5 sm:p-8",
    TITLE: "text-sm text-guardian-header",
    LABEL: "block text-sm font-medium text-guardian-body mb-2",
    LINK: "text-rag-green font-semibold hover:text-[#0B7A72] transition-colors",
    SUBMIT:
      "inline-flex w-full items-center justify-center min-h-11 px-4 py-2 rounded-full bg-rag-green text-sm font-semibold text-white transition-colors hover:bg-[#0B7A72] focus:outline-none focus:ring-2 focus:ring-rag-green/40 disabled:opacity-50 disabled:cursor-not-allowed",
  },
  CARDS: {
    BASE: "bg-white rounded-xl shadow-sm",
    WITH_BORDER: "bg-white rounded-xl border border-gray-200",
    OVERFLOW_HIDDEN: "overflow-hidden",
  },
  INPUTS: {
    BASE: "w-full h-11 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
    PATIENT:
      "w-full min-h-11 px-3 py-2 border border-guardian-20 rounded-xl text-sm text-guardian-body bg-white focus:outline-none focus:ring-2 focus:ring-[#F43F5E]/30 focus:border-[#F43F5E]",
    TEXTAREA:
      "w-full min-h-[120px] px-3 py-2 border border-guardian-20 rounded-xl text-sm text-guardian-body bg-white placeholder:text-guardian-header focus:outline-none focus:ring-2 focus:ring-[#F43F5E]/30 focus:border-[#F43F5E]",
    FILE: "block w-full text-sm text-guardian-body file:mr-3 file:rounded-full file:border-0 file:bg-guardian-10 file:px-3 file:py-2 file:text-sm file:font-medium file:text-[#02040A] hover:file:bg-guardian-20",
    ERROR: "border-rag-red focus:ring-rag-red/30 focus:border-rag-red",
  },
  TEXT: {
    ERROR: "text-rag-red text-sm",
    SUCCESS: "text-green-600 text-sm",
    MUTED: "text-sm text-guardian-header",
  },
  RISK_BADGES: {
    HIGH: "bg-red-100 text-red-800",
    MEDIUM: "bg-yellow-100 text-yellow-800",
    LOW: "bg-green-100 text-green-800",
  },
  LAYOUTS: {
    CONTAINER: "mx-auto px-4 sm:px-6 lg:px-8 py-8",
    FLEX_CENTER: "flex items-center justify-center",
    FULL_HEIGHT: "min-h-screen",
  },
  PATIENT_LIST_TABLE: {
    CARD: "bg-white border border-guardian-20 rounded-2xl overflow-hidden w-full",
    WRAPPER: "hidden md:block min-w-0 overflow-x-auto",
    TABLE: "w-full min-w-[1372px] table-fixed border-collapse",
    HEADER_ROW: "h-11 border-b-2 border-guardian-20",
    HEADER_CELL:
      "h-11 max-h-11 box-border pl-6 pr-2 text-left text-xs font-normal text-guardian-header tracking-[-0.01em]",
    HEADER_CELL_STATIC:
      "h-11 max-h-11 box-border pl-6 pr-2 text-left text-xs font-normal text-guardian-header tracking-[-0.01em]",
    HEADER_CELL_ACTIONS:
      "h-11 max-h-11 box-border pl-4 pr-6 text-left text-xs font-normal text-guardian-header tracking-[-0.01em]",
    BODY_ROW: "h-11 border-b border-guardian-20 bg-white hover:bg-guardian-10",
    BODY_CELL:
      "h-11 max-h-11 box-border pl-6 pr-2 max-w-0 truncate text-sm font-normal text-guardian-body tracking-[-0.013em]",
    BODY_CELL_BADGE:
      "h-11 max-h-11 box-border pl-6 pr-2 whitespace-nowrap align-middle",
    BODY_CELL_ACTIONS:
      "h-11 max-h-11 box-border pl-4 pr-6 whitespace-nowrap align-middle",
    ACTION_FAB:
      "w-8 h-8 rounded-full bg-guardian-10 hover:bg-guardian-20 flex items-center justify-center focus:outline-none transition text-[#02040A]",
  },
  DROPDOWN_MENU: {
    PANEL:
      "absolute z-[70] mt-1.5 min-w-[180px] overflow-hidden rounded-xl border border-guardian-20 bg-white py-1 shadow-lg",
    PANEL_LEFT: "top-full left-0 w-full",
    PANEL_RIGHT: "top-full right-0 w-48",
    ITEM: "w-full text-left px-3 py-2 text-xs font-semibold text-[#02040A] transition-colors hover:bg-guardian-10",
    ITEM_SELECTED: "bg-guardian-10",
  },
} as const;

// Default Values
export const DEFAULTS = {
  HOSPITAL_NAME: "Hospital User",
} as const;

export type FilterOption = (typeof FILTER_OPTIONS)[keyof typeof FILTER_OPTIONS];
export type RiskLevel = (typeof RISK_LEVELS)[keyof typeof RISK_LEVELS];
export type SortDirection =
  (typeof SORT_OPTIONS.DIRECTIONS)[keyof typeof SORT_OPTIONS.DIRECTIONS];
